import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { trackLink } from "@/lib/analytics";
import "./PipelineAccordion.css";

const PANELS = [
  {
    id: "ingest",
    title: "Ingest",
    subtitle: "Fax, scan, or API",
    desc: "Every claim that reaches your fax line, mailroom, or SFTP drop lands in one queue, whatever its quality.",
    bullets: ["Fax, PDF, photo, and API upload", "Red drop-out ink forms handled natively", "Auto-sorted by form type"],
    pattern: "diag",
  },
  {
    id: "extract",
    title: "Extract",
    subtitle: "Every box, read like a biller",
    desc: "A vision-language model reads the whole form, not one character at a time, so nothing is guessed from fragments.",
    bullets: ["Checkboxes, ticks, and crosses", "Handwriting, signatures, and stamps", "Watermarks and form lines ignored"],
    pattern: "dots",
  },
  {
    id: "validate",
    title: "Validate",
    subtitle: "Catch errors before denials",
    desc: "Each field is checked and scored before data leaves the pipeline. Doubtful fields go to a person, not to the payer.",
    bullets: ["NPI, ICD-10, and CPT checks", "Payer edits applied", "Per-field confidence with human review"],
    pattern: "horiz",
  },
  {
    id: "convert",
    title: "Convert",
    subtitle: "CMS-1500 to 837P",
    desc: "Each box maps to its X12 loop and segment, so a scanned claim becomes a submittable file with no re-keying.",
    bullets: ["Box 24 lines to Loop 2400", "Box 21 diagnoses to Loop 2300 HI", "Providers to Loops 2310B and 2010AA"],
    pattern: "grid",
  },
  {
    id: "deliver",
    title: "Deliver",
    subtitle: "To your clearinghouse or EHR",
    desc: "Send structured JSON or 837P to the systems you already use, with confidence scores attached.",
    bullets: ["REST API and SDK", "Webhook and SFTP delivery", "Hosted, private cloud, or on-premise"],
    pattern: "arcs",
  },
  {
    id: "fine-tune",
    title: "Fine-tune",
    subtitle: "Train on your own forms",
    desc: "Teach the model your form variants, payer templates, or new document types from a handful of labeled samples. No ML team needed.",
    bullets: ["Upload 20 to 200 labeled examples", "One click starts the fine-tuning run", "Your model goes live behind the same API"],
    pattern: "diamond",
  },
];

const IngestVisual = () => (
  <>
    <div className="pv-chips">
      {["Fax", "PDF", "Photo", "SFTP"].map((c) => (
        <span key={c} className="pv-chip">{c}</span>
      ))}
    </div>
    <div className="pv-ingest-bar">
      <span>CMS-1500</span>
      <span>4 sources, 1 queue</span>
    </div>
  </>
);

const ExtractVisual = () => (
  <div className="pv-form pv-form-grid">
    <div className="pv-scan" />
    <div className="pv-frow">
      <div className="pv-cell">
        <span className="pv-flabel">Patient</span>
        <span className="pv-fval">JANE SMITH</span>
      </div>
      <div className="pv-cell">
        <span className="pv-flabel">Box 11d</span>
        <span className="pv-fval"><span className="pv-checkbox"><Check className="h-3 w-3" strokeWidth={3} /></span></span>
      </div>
    </div>
    <div className="pv-frow">
      <div className="pv-cell">
        <span className="pv-flabel">DOB</span>
        <span className="pv-fval">03/14/1981</span>
      </div>
      <div className="pv-cell">
        <span className="pv-flabel">Box 21</span>
        <span className="pv-fval">E11.9</span>
      </div>
    </div>
    <div className="pv-frow">
      <div className="pv-cell">
        <span className="pv-flabel">Box 24 CPT</span>
        <span className="pv-fval">99213</span>
      </div>
      <div className="pv-cell">
        <span className="pv-flabel">Charge</span>
        <span className="pv-fval">$185.00</span>
      </div>
    </div>
  </div>
);

const ValidateVisual = () => (
  <>
    {[
      ["NPI", "1234567890"],
      ["ICD-10", "E11.9"],
      ["CPT", "99213"],
    ].map(([label, code]) => (
      <div key={label} className="pv-vrow">
        <span className="pv-vlabel">{label}<code>{code}</code></span>
        <span className="pv-vstatus ok">
          <span className="pv-vdot" />
          <Check className="pv-vcheck h-3.5 w-3.5" strokeWidth={3} />
          Valid
        </span>
      </div>
    ))}
    <div className="pv-vrow">
      <span className="pv-vlabel">Signature date</span>
      <span className="pv-vstatus review">
        <span className="pv-vdot" />
        0.71, sent to review
      </span>
    </div>
  </>
);

const ConvertVisual = () => (
  <pre className="pv-code">
    <span className="pv-cline"><span>ST*837*0001*005010X222A1~</span></span>
    <span className="pv-cline"><span>CLM*PATIENT01*185.00***11:B:1~</span></span>
    <span className="pv-cline"><span>HI*ABK:E119~<s className="pv-ctag">Box 21</s></span></span>
    <span className="pv-cline"><span>SV1*HC:99213*185.00*UN*1***1~<s className="pv-ctag">Box 24</s></span></span>
    <span className="pv-cline"><span>DTP*472*D8*20250114~</span></span>
  </pre>
);

const DeliverVisual = () => (
  <>
    <pre className="pv-json">
      <span>{"{"}</span>{"\n"}
      {"  "}<span className="k">"claim_id"</span>: "PATIENT01",{"\n"}
      {"  "}<span className="k">"icd10"</span>: ["E11.9"], <span className="k">"cpt"</span>: "99213",{"\n"}
      {"  "}<span className="k">"charge"</span>: 185.00, <span className="k">"confidence"</span>: 0.994{"\n"}
      <span>{"}"}</span>
    </pre>
    <div className="pv-pills">
      {["API", "Webhook", "SFTP"].map((p) => (
        <span key={p} className="pv-pill">{p}</span>
      ))}
    </div>
  </>
);

const FineTuneVisual = () => (
  <>
    {[
      ["Upload labeled samples", "20 to 200 forms"],
      ["Click Fine-tune", "no config, no code"],
      ["Ship the model", "same endpoint"],
    ].map(([main, note], i) => (
      <div key={main} className="pv-step">
        <span className="pv-badge">{i + 1}</span>
        <span>
          <span className="font-semibold">{main}</span>
          <span className="block text-xs" style={{ color: "var(--pipe-muted)" }}>{note}</span>
        </span>
      </div>
    ))}
  </>
);

const VISUALS = {
  ingest: IngestVisual,
  extract: ExtractVisual,
  validate: ValidateVisual,
  convert: ConvertVisual,
  deliver: DeliverVisual,
  "fine-tune": FineTuneVisual,
};

export const PipelineAccordion = () => {
  const [open, setOpen] = useState(0);
  const btnRefs = useRef([]);

  const openCard = (i) => setOpen(i);

  const onBarEnd = (e) => {
    if (e.animationName === "pipeBar") setOpen((o) => (o + 1) % PANELS.length);
  };

  const onKeyDown = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next = (open + (e.key === "ArrowRight" ? 1 : -1) + PANELS.length) % PANELS.length;
    openCard(next);
    btnRefs.current[next]?.focus();
  };

  return (
    <section
      className="pipe-section"
      aria-label="Claims processing pipeline"
      data-testid="pipeline-section"
      onKeyDown={onKeyDown}
    >
      <div className="mx-auto max-w-[1260px] px-5 py-24 sm:px-8">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Paper claims in. 837P out.
        </h2>
        <p className="mt-4 max-w-2xl leading-relaxed text-slate-600">
          Hewto.ai reads scanned, faxed, and handwritten CMS-1500 forms, checks every field, and
          delivers a clean 837P file. Follow one claim through the pipeline.
        </p>
        <div className="pipe-cta">
          <Link
            to="/demo/hcfa-cms-1500"
            className="pipe-cta-primary"
            data-testid="pipeline-cta-try-own-claim"
            onClick={() => trackLink("pipeline_cta_try_own_claim")}
          >
            Try it on your own claim
          </Link>
          <Link
            to="/contact-sales"
            className="pipe-cta-secondary"
            data-testid="pipeline-cta-talk-to-sales"
            onClick={() => trackLink("pipeline_cta_talk_to_sales")}
          >
            Talk to sales
          </Link>
        </div>
        <p className="pipe-cta-note" data-testid="pipeline-cta-note">
          Use a sample or de-identified claim.
        </p>

        <div className="pipe-row">
          {PANELS.map((p, i) => {
            const isOpen = i === open;
            const Visual = VISUALS[p.id];
            return (
              <React.Fragment key={p.id}>
                <div
                  className={`pipe-card${isOpen ? " open" : ""}`}
                  data-pattern={p.pattern}
                  data-testid={`pipeline-card-${p.id}`}
                  onMouseEnter={() => openCard(i)}
                >
                <button
                  ref={(el) => (btnRefs.current[i] = el)}
                  className="pipe-card-head"
                  aria-expanded={isOpen}
                  aria-controls={`pipeline-body-${p.id}`}
                  data-testid={`pipeline-head-${p.id}`}
                  onClick={() => openCard(i)}
                >
                  <h3 className="pipe-card-title">{p.title}</h3>
                  <span className="pipe-rule" aria-hidden="true" />
                  <p className="pipe-card-sub">{p.subtitle}</p>
                </button>
                <div
                  id={`pipeline-body-${p.id}`}
                  className="pipe-card-body"
                  data-testid={`pipeline-body-${p.id}`}
                >
                  <p className="pipe-card-desc">{p.desc}</p>
                  <ul className="pipe-bullets">
                    {p.bullets.map((b) => (
                      <li key={b}>
                        <span className="pipe-tick" aria-hidden="true">
                          <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  {isOpen && (
                    <div className="pipe-visual" aria-hidden="true" data-testid={`pipeline-visual-${p.id}`}>
                      <Visual />
                    </div>
                  )}
                </div>
                {isOpen && (
                  <div className="pipe-progress" data-testid={`pipeline-progress-${p.id}`}>
                    <div className="pipe-progress-fill" onAnimationEnd={onBarEnd} />
                  </div>
                )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PipelineAccordion;
